import { useEffect, useState } from 'react';
import type { PendingApproval, RegisterRef } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney } from '../format';

interface Props {
  onBack: () => void;
}

// Mühasib (KT-61): satışsız müştəri verilmələrinin təsdiqi. Nağd/kart açıq növbəli kassaya; köçürmə və qalan nisyədə.
// Hər verilmə yalnız bir dəfə təsdiqlənir; eyni basış eyni UUID ilə gedir.
export function Approvals({ onBack }: Props) {
  const [pending, setPending] = useState<PendingApproval[] | null>(null);
  const [registers, setRegisters] = useState<RegisterRef[]>([]);
  const [registerId, setRegisterId] = useState('');
  const [parts, setParts] = useState<Record<string, { cash: string; card: string; transfer: string }>>({});
  const [ids, setIds] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function reload() {
    return api
      .approvalsPending()
      .then((p) => {
        setPending(p);
        setParts((cur) => Object.fromEntries(p.map((r) => [r.docId, cur[r.docId] ?? { cash: '0', card: '0', transfer: '0' }])));
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'));
  }

  useEffect(() => {
    reload();
    api.shiftRegisters().then((r) => {
      setRegisters(r);
      if (r.length > 0) setRegisterId(r[0].id);
    }).catch(() => setRegisters([]));
  }, []);

  function setPart(docId: string, key: 'cash' | 'card' | 'transfer', value: string) {
    setParts((cur) => ({ ...cur, [docId]: { ...(cur[docId] ?? { cash: '0', card: '0', transfer: '0' }), [key]: value } }));
  }

  async function approve(row: PendingApproval) {
    const p = parts[row.docId] ?? { cash: '0', card: '0', transfer: '0' };
    const cash = Number(p.cash) || 0;
    const card = Number(p.card) || 0;
    const transfer = Number(p.transfer) || 0;
    if (cash + card + transfer > row.total) return setError(`Ödəniş cəmi ${formatMoney(row.total)}-dən çox ola bilməz`);
    if ((cash > 0 || card > 0) && !registerId) return setError('Nağd və ya kart üçün kassa seçin');
    const id = ids[row.docId] ?? crypto.randomUUID();
    setIds((cur) => ({ ...cur, [row.docId]: id }));
    setBusy(true);
    setError(null);
    try {
      const r = await api.approve(row.docId, { id, registerId: registerId || null, cash, card, transfer, note: notes[row.docId]?.trim() || null });
      setNotice(`${row.displayNo}: ${r.displayNo} təsdiqləndi — nisyə ${formatMoney(r.credit)}`);
      setIds((cur) => {
        const next = { ...cur };
        delete next[row.docId];
        return next;
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Təsdiq alınmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">Təsdiq gözləyənlər</span>
        <span className="topbar-spacer" />
        <button type="button" className="link-btn back-home" onClick={onBack}>
          Geri
        </button>
      </header>
      <main className="content">
        <section className="card card-wide">
          <h2>Təsdiq gözləyənlər</h2>
          <p className="muted small">Anbardar müştəriyə malı verdi. Ödənilən hissəni daxil edin; qalanı müştərinin nisyəsində qalır.</p>
          <div className="form-grid">
            <label>
              Kassa (nağd / kart üçün)
              <select value={registerId} onChange={(e) => setRegisterId(e.target.value)} disabled={busy || registers.length === 0}>
                {registers.length === 0 && <option value="">Kassa yoxdur</option>}
                {registers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} {r.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {notice && <p className="ok" aria-live="polite">{notice}</p>}
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          {!pending && !error && <p className="muted">Yüklənir…</p>}
          {pending && pending.length === 0 && <p className="muted">Təsdiq gözləyən verilmə yoxdur.</p>}
          {pending && pending.length > 0 && (
            <ul className="product-list">
              {pending.map((row) => {
                const p = parts[row.docId] ?? { cash: '0', card: '0', transfer: '0' };
                return (
                  <li key={row.docId} className="product-row">
                    <span className="product-name">{row.customerName}</span>
                    <span className="muted small">
                      {row.displayNo} · {row.waitingDays} gün · cəmi {formatMoney(row.total)}
                    </span>
                    <label>
                      Nağd
                      <input type="number" min="0" step="0.01" aria-label={`${row.displayNo} nağd`} value={p.cash} onChange={(e) => setPart(row.docId, 'cash', e.target.value)} disabled={busy} />
                    </label>
                    <label>
                      Kart
                      <input type="number" min="0" step="0.01" aria-label={`${row.displayNo} kart`} value={p.card} onChange={(e) => setPart(row.docId, 'card', e.target.value)} disabled={busy} />
                    </label>
                    <label>
                      Köçürmə
                      <input type="number" min="0" step="0.01" aria-label={`${row.displayNo} köçürmə`} value={p.transfer} onChange={(e) => setPart(row.docId, 'transfer', e.target.value)} disabled={busy} />
                    </label>
                    <label>
                      Qeyd
                      <input aria-label={`${row.displayNo} qeyd`} maxLength={500} value={notes[row.docId] ?? ''} onChange={(e) => setNotes((cur) => ({ ...cur, [row.docId]: e.target.value }))} disabled={busy} />
                    </label>
                    <button type="button" className="btn-small" onClick={() => approve(row)} disabled={busy}>
                      Təsdiq et
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
